// Symptoms are persisted as a single newline-joined string (same API field
// as before - no backend change needed), with each line optionally prefixed
// to carry its state: "[YES] Fatigue" for a confirmed/positive symptom, or a
// plain "Nausea" line for the default negative/not-reported state (no prefix
// needed for the default, so most saved data stays close to plain text).
// Any reader that doesn't know about this (the older careplan.js view, admin
// template screens, an unmigrated PDF path) still shows a readable line
// instead of breaking, since the prefix by itself reads fine as plain text.
//
// SCRUM-755 simplifies this from the earlier 3-state (yes/no/unset) control
// down to a 2-state one: every symptom defaults to negative (X), one click
// confirms it positive (check), a second click reverts it - there is no
// longer a neutral/unset state. Any already-saved line from the old 3-state
// version that had no [YES]/[NO] prefix (i.e. was "unset") is treated as
// negative/X here, matching the new default.

export const SYMPTOM_STATES = {
  YES: "yes",
  NO: "no",
};

const PREFIX_YES = "[YES] ";
// Matches both the current "[YES] " marker and the old 3-state version's
// "[NO] " marker (read-only compat - never written again, see
// serializeSymptomLine below).
const LINE_PATTERN = /^\[(YES|NO)\]\s*(.*)$/i;

// AI-generated care plans mark each symptom line with a trailing
// "{Reported}" / "{Not-Reported}" (or "{Not Reported}") marker instead of
// the "[YES]" prefix above - read-only compat, same as the old "[NO]"
// prefix: recognized here so it renders through the same check/X glyph
// with the raw marker never shown, but never written back out (see
// serializeSymptomLine below, which only ever emits the "[YES] " form).
const SUFFIX_PATTERN = /\s*\{\s*(not[\s-]?reported|reported)\s*\}\s*$/i;

// SCRUM-788: AI-supplied qualifier text on a symptom - e.g. a Care Manager
// tells the AI chat "headache not reported by patient" and the backend
// returns the line as "headache — not reported" instead of the old
// "headache{Not-Reported}" suffix marker above. Splits on the FIRST em dash
// only (an em dash - not a plain hyphen - specifically, so an ordinary
// hyphenated symptom name like "Post-nasal drip" is never mistaken for a
// qualifier). qualifier is null when there isn't one, so callers can tell a
// plain manually-typed symptom apart from an AI-qualified one.
const QUALIFIER_SEPARATOR_PATTERN = /\s+—\s+/;

export const splitSymptomQualifier = (name) => {
  const raw = name || "";
  const match = raw.match(QUALIFIER_SEPARATOR_PATTERN);
  if (!match) return { name: raw, qualifier: null };
  return {
    name: raw.slice(0, match.index).trim(),
    qualifier: raw.slice(match.index + match[0].length).trim(),
  };
};

export const parseSymptomLine = (line) => {
  const raw = line || "";
  const suffixMatch = raw.match(SUFFIX_PATTERN);
  if (suffixMatch) {
    const state = /^not/i.test(suffixMatch[1]) ? SYMPTOM_STATES.NO : SYMPTOM_STATES.YES;
    return { ...splitSymptomQualifier(raw.slice(0, suffixMatch.index).trim()), state };
  }
  const match = raw.match(LINE_PATTERN);
  if (!match) return { ...splitSymptomQualifier(raw.trim()), state: SYMPTOM_STATES.NO };
  const state = match[1].toUpperCase() === "YES" ? SYMPTOM_STATES.YES : SYMPTOM_STATES.NO;
  return { ...splitSymptomQualifier(match[2].trim()), state };
};

// Splits on newline into one entry per row. Deliberately does NOT drop a
// trailing blank line the way NumberedTextArea's own load-time trimming
// does: pressing Enter after the last symptom appends a genuinely blank new
// row at the end of the string (serializeSymptoms below), and that row
// needs to survive being parsed back out on the very next render, or Enter
// would silently do nothing whenever used at the end of the list - which is
// the most common case (adding one more symptom after the last one).
export const parseSymptomsString = (value) => {
  const lines = (value || "").split("\n");
  if (lines.length === 0) return [{ name: "", state: SYMPTOM_STATES.NO }];
  return lines.map(parseSymptomLine);
};

// Negative (the default) is written unprefixed - only a positive/confirmed
// symptom needs the "[YES] " marker. Kept accepting the old "[NO] " prefix
// on read (LINE_PATTERN above) for any already-saved rows from before this
// simplification, but never writes it again going forward.
export const serializeSymptomLine = ({ name, state, qualifier }) => {
  const fullName = qualifier ? `${name} — ${qualifier}` : name;
  return state === SYMPTOM_STATES.YES ? `${PREFIX_YES}${fullName}` : fullName;
};

export const serializeSymptoms = (symptoms) =>
  symptoms.map(serializeSymptomLine).join("\n");

// Simple two-state toggle per SCRUM-755: X (negative/default) <-> check
// (positive/confirmed). Anything that isn't YES is treated as the default
// negative state, so a stray/legacy value always flips to YES on click.
export const cycleSymptomState = (state) =>
  state === SYMPTOM_STATES.YES ? SYMPTOM_STATES.NO : SYMPTOM_STATES.YES;

// Per direct request (2026-09-25): the Symptoms tri-state checkbox only
// applies to care plans created on/after this date - older plans may
// predate the AI's "{Reported}"/"{Not-Reported}" convention (SUFFIX_PATTERN
// above) entirely, so every renderer of Symptoms (the wizard, both PDF
// exports) falls back to plain text for them instead. Deliberately a FIXED
// date (the day this feature shipped), not a moving "today" - re-doing
// this same change on a fresh branch off main at a later date should
// update this constant to THAT day's date, not leave it as this one.
export const SYMPTOMS_CHECKBOX_CUTOFF_DATE_PST = "2026-10-13";

// Calendar-date string comparison (not Date-object comparison) sidesteps
// timezone-conversion pitfalls entirely: both sides are already "YYYY-MM-DD
// in PST" strings, and that format sorts correctly as plain strings.
export const wasCreatedOnOrAfterSymptomsCutoff = (createdAt) => {
  if (!createdAt) return false;
  const createdDatePST = new Date(createdAt).toLocaleDateString("en-CA", {
    timeZone: "America/Los_Angeles",
  });
  const result = createdDatePST >= SYMPTOMS_CHECKBOX_CUTOFF_DATE_PST;
  // TEMPORARY - remove once the PDF symptoms-checkbox mismatch is diagnosed.
  console.log("[symptoms-cutoff]", { raw: createdAt, createdDatePST, cutoff: SYMPTOMS_CHECKBOX_CUTOFF_DATE_PST, result });
  return result;
};
